import maplibregl from 'maplibre-gl'
import geomagnetism from 'geomagnetism'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'
import { isGeoJSONSource } from '../../components/map/map-engine.js'
import { CACHED_GLYPHS } from '../tile-cache/vector-style-cache-service.js'

const SOURCE_ID = 'declination-source'
const LINE_LAYER_ID = 'declination-lines'
const LABEL_LAYER_ID = 'declination-labels'
const DEFAULT_OPACITY = 1.0

// Sample grid must be 3-4x finer than contour interval for marching squares
function sampleStep(zoom: number): number {
  if (zoom < 3) return 1.5
  if (zoom < 5) return 0.5
  if (zoom < 6) return 0.25
  if (zoom < 8) return 0.15
  if (zoom < 10) return 0.08
  return 0.04
}

function contourInterval(zoom: number): number {
  if (zoom < 3) return 5
  if (zoom < 5) return 2
  if (zoom < 6) return 1
  if (zoom < 8) return 0.5
  return 0.25
}

interface GridCell {
  lng: number
  lat: number
  dec: number
}

// Marching squares isoline extraction
function extractIsolines(
  grid: GridCell[][],
  rows: number,
  cols: number,
  _step: number,
  level: number
): GeoJSON.Feature[] {
  const features: GeoJSON.Feature[] = []
  const segments: [number, number][][] = []

  for (let r = 0; r < rows - 1; r++) {
    for (let c = 0; c < cols - 1; c++) {
      const tl = grid[r][c]
      const tr = grid[r][c + 1]
      const bl = grid[r + 1][c]
      const br = grid[r + 1][c + 1]

      const code =
        (tl.dec >= level ? 8 : 0) |
        (tr.dec >= level ? 4 : 0) |
        (br.dec >= level ? 2 : 0) |
        (bl.dec >= level ? 1 : 0)

      if (code === 0 || code === 15) continue

      const interp = (a: GridCell, b: GridCell): [number, number] => {
        const t = (level - a.dec) / (b.dec - a.dec)
        return [a.lng + t * (b.lng - a.lng), a.lat + t * (b.lat - a.lat)]
      }

      const top = () => interp(tl, tr)
      const right = () => interp(tr, br)
      const bottom = () => interp(bl, br)
      const left = () => interp(tl, bl)

      const addSeg = (a: [number, number], b: [number, number]) => segments.push([a, b])

      switch (code) {
        case 1:
        case 14:
          addSeg(left(), bottom())
          break
        case 2:
        case 13:
          addSeg(bottom(), right())
          break
        case 3:
        case 12:
          addSeg(left(), right())
          break
        case 4:
        case 11:
          addSeg(top(), right())
          break
        case 5:
          addSeg(left(), top())
          addSeg(bottom(), right())
          break
        case 6:
        case 9:
          addSeg(top(), bottom())
          break
        case 7:
        case 8:
          addSeg(left(), top())
          break
        case 10:
          addSeg(top(), right())
          addSeg(left(), bottom())
          break
      }
    }
  }

  // Join adjacent segments into linestrings
  const joined = joinSegments(segments)
  for (const coords of joined) {
    if (coords.length < 2) continue
    features.push({
      type: 'Feature',
      properties: {
        type: 'isogonic',
        declination: level,
        label: `${level > 0 ? '+' : ''}${level}°`,
      },
      geometry: { type: 'LineString', coordinates: coords },
    })
  }
  return features
}

function joinSegments(segments: [number, number][][]): [number, number][][] {
  if (segments.length === 0) return []
  const lines: [number, number][][] = []
  const used = new Set<number>()

  for (let i = 0; i < segments.length; i++) {
    if (used.has(i)) continue
    used.add(i)
    const line = [...segments[i]]

    let extended = true
    while (extended) {
      extended = false
      for (let j = 0; j < segments.length; j++) {
        if (used.has(j)) continue
        const seg = segments[j]
        const last = line[line.length - 1]
        const first = line[0]

        if (near(last, seg[0])) {
          line.push(seg[1])
          used.add(j)
          extended = true
        } else if (near(last, seg[1])) {
          line.push(seg[0])
          used.add(j)
          extended = true
        } else if (near(first, seg[1])) {
          line.unshift(seg[0])
          used.add(j)
          extended = true
        } else if (near(first, seg[0])) {
          line.unshift(seg[1])
          used.add(j)
          extended = true
        }
      }
    }
    lines.push(line)
  }
  return lines
}

function near(a: [number, number], b: [number, number]): boolean {
  return Math.abs(a[0] - b[0]) < 0.001 && Math.abs(a[1] - b[1]) < 0.001
}

function buildDeclinationGrid(
  bounds: maplibregl.LngLatBounds,
  zoom: number
): GeoJSON.FeatureCollection {
  const step = sampleStep(zoom)
  const interval = contourInterval(zoom)
  const model = geomagnetism.model()

  const west = Math.floor(bounds.getWest() / step) * step - step
  const east = Math.ceil(bounds.getEast() / step) * step + step
  const south = Math.max(-85, Math.floor(bounds.getSouth() / step) * step - step)
  const north = Math.min(85, Math.ceil(bounds.getNorth() / step) * step + step)

  const grid: GridCell[][] = []
  const rows: number[] = []
  const cols: number[] = []

  for (let lat = north; lat >= south; lat -= step) {
    rows.push(lat)
  }
  for (let lng = west; lng <= east; lng += step) {
    cols.push(lng)
  }

  let minDec = Infinity
  let maxDec = -Infinity

  for (let r = 0; r < rows.length; r++) {
    grid[r] = []
    for (let c = 0; c < cols.length; c++) {
      const info = model.point([rows[r], cols[c]])
      const dec = info.decl
      grid[r][c] = { lng: cols[c], lat: rows[r], dec }
      if (dec < minDec) minDec = dec
      if (dec > maxDec) maxDec = dec
    }
  }

  const features: GeoJSON.Feature[] = []

  const startLevel = Math.floor(minDec / interval) * interval
  const endLevel = Math.ceil(maxDec / interval) * interval

  for (let level = startLevel; level <= endLevel; level += interval) {
    features.push(...extractIsolines(grid, rows.length, cols.length, step, level))
  }

  // Agonic line (0° declination) - add as special feature
  const agonicFeatures = features.filter(
    f => f.properties && (f.properties as Record<string, unknown>).declination === 0
  )
  for (const f of agonicFeatures) {
    ;(f.properties as Record<string, unknown>).isAgonic = true
  }

  return { type: 'FeatureCollection', features }
}

class DeclinationService {
  private map: maplibregl.Map | null = null
  private _enabled = false
  private _opacity = DEFAULT_OPACITY
  private updateHandler = () => this.update()

  get enabled(): boolean {
    return this._enabled
  }
  get opacity(): number {
    return this._opacity
  }

  register(map: maplibregl.Map): void {
    this.map = map
  }

  unregister(): void {
    this.disable()
    this.map = null
  }

  enable(): void {
    if (!this.map || this._enabled) return
    this._enabled = true
    this.addSourceAndLayers()
    this.map.on('moveend', this.updateHandler)
    this.update()
  }

  disable(): void {
    if (!this.map) return
    this._enabled = false
    this.map.off('moveend', this.updateHandler)
    this.removeSourceAndLayers()
  }

  toggle(): void {
    if (this._enabled) this.disable()
    else this.enable()
  }

  setOpacity(opacity: number): void {
    this._opacity = Math.max(0, Math.min(1, opacity))
    if (!this.map || !(this.map as unknown as Record<string, unknown>).style) return
    if (this.map.getLayer(LINE_LAYER_ID)) {
      this.map.setPaintProperty(LINE_LAYER_ID, 'line-opacity', this._opacity)
    }
    if (this.map.getLayer(LABEL_LAYER_ID)) {
      this.map.setPaintProperty(LABEL_LAYER_ID, 'text-opacity', this._opacity)
    }
  }

  reAdd(): void {
    if (!this._enabled || !this.map) return
    this.removeSourceAndLayers()
    this.addSourceAndLayers()
    this.map.on('moveend', this.updateHandler)
    this.update()
  }

  private update(): void {
    if (!this.map || !this._enabled) return
    const bounds = this.map.getBounds()
    const zoom = this.map.getZoom()
    const data = buildDeclinationGrid(bounds, zoom)
    const source = this.map.getSource(SOURCE_ID)
    if (isGeoJSONSource(source)) source.setData(data)
  }

  private addSourceAndLayers(): void {
    if (!this.map) return
    this.ensureGlyphs()

    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
    }

    if (!this.map.getLayer(LINE_LAYER_ID)) {
      addOverlayLayer(
        this.map,
        {
          id: LINE_LAYER_ID,
          type: 'line',
          source: SOURCE_ID,
          filter: ['==', ['get', 'type'], 'isogonic'],
          paint: {
            'line-color': [
              'case',
              ['==', ['get', 'isAgonic'], true],
              '#D32F2F',
              ['<', ['get', 'declination'], 0],
              '#1565C0',
              '#C62828',
            ],
            'line-opacity': this._opacity,
            'line-width': ['case', ['==', ['get', 'isAgonic'], true], 4, 2.5],
          },
        },
        'declination'
      )
    }

    if (!this.map.getLayer(LABEL_LAYER_ID)) {
      addOverlayLayer(
        this.map,
        {
          id: LABEL_LAYER_ID,
          type: 'symbol',
          source: SOURCE_ID,
          filter: ['==', ['get', 'type'], 'isogonic'],
          layout: {
            'symbol-placement': 'line',
            'text-field': ['get', 'label'],
            'text-font': ['Noto Sans Regular'],
            'text-size': 11,
            'text-max-angle': 30,
            'text-padding': 30,
            'text-allow-overlap': false,
          },
          paint: {
            'text-color': [
              'case',
              ['==', ['get', 'isAgonic'], true],
              '#D32F2F',
              ['<', ['get', 'declination'], 0],
              '#1565C0',
              '#C62828',
            ],
            'text-opacity': this._opacity,
            'text-halo-color': 'rgba(255, 255, 255, 0.9)',
            'text-halo-width': 2,
          },
        },
        'declination'
      )
    }
  }

  private ensureGlyphs(): void {
    if (!this.map) return
    const style = this.map.getStyle()
    if (!style.glyphs) {
      style.glyphs = CACHED_GLYPHS
      this.map.setStyle(style, { diff: true })
    }
  }

  private removeSourceAndLayers(): void {
    if (!this.map) return
    if (this.map.getLayer(LABEL_LAYER_ID)) this.map.removeLayer(LABEL_LAYER_ID)
    if (this.map.getLayer(LINE_LAYER_ID)) this.map.removeLayer(LINE_LAYER_ID)
    if (this.map.getSource(SOURCE_ID)) this.map.removeSource(SOURCE_ID)
  }
}

export const declinationService = new DeclinationService()
